import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import AuthContext from '../shared/AuthContext';
import { authService } from '../shared/hooks/AuthService';
import { getOrCreateDeviceId } from '../../utils/deviceId';

const ENV_CLIENT_ID = (process.env.REACT_APP_GOOGLE_CLIENT_ID || '').trim();

function loadGsiScript() {
  if (window.google?.accounts?.id) {
    return Promise.resolve();
  }
  const existing = document.querySelector('script[data-qnaread-gsi]');
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Could not load Google Sign-In')), { once: true });
    });
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.dataset.qnareadGsi = '1';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Could not load Google Sign-In'));
    document.head.appendChild(script);
  });
}

export default function GoogleSignInButton({ onError, disabled }) {
  const btnRef = useRef(null);
  const renderedRef = useRef(false);
  const { googleLogin } = useContext(AuthContext);
  const [resolvedClientId, setResolvedClientId] = useState(ENV_CLIENT_ID);
  const [scriptReady, setScriptReady] = useState(false);
  const [available, setAvailable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [initError, setInitError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const statusRes = await authService.oauthStatus();
        const data = statusRes.data || {};
        if (cancelled) return;

        const fromApi = (data.clientId || '').trim();
        const resolved = ENV_CLIENT_ID || fromApi;

        if (!data.google) {
          setInitError('Google sign-in is not configured on the API (qnaread.oauth.google.client-id).');
          return;
        }
        if (!resolved) {
          setInitError(
            'Add REACT_APP_GOOGLE_CLIENT_ID to .env.development, or set the client ID on the API and restart both servers.'
          );
          return;
        }

        setResolvedClientId(resolved);
        await loadGsiScript();
        if (cancelled) return;
        if (!window.google?.accounts?.id) {
          setInitError('Google Sign-In script loaded but is unavailable. Check ad blockers or network.');
          return;
        }
        setScriptReady(true);
      } catch {
        if (!cancelled) {
          setInitError('Could not reach the API for Google sign-in. Is the backend running on port 8084?');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useLayoutEffect(() => {
    if (initError || !scriptReady || !resolvedClientId || renderedRef.current) return;
    const el = btnRef.current;
    if (!el || !window.google?.accounts?.id) return;

    el.innerHTML = '';
    window.google.accounts.id.initialize({
      client_id: resolvedClientId,
      callback: async (response) => {
        if (!response?.credential || disabled) return;
        setBusy(true);
        onError?.(null);
        const deviceId = getOrCreateDeviceId();
        const result = await googleLogin({ credential: response.credential, deviceId });
        if (!result.success) onError?.(result.error);
        setBusy(false);
      },
    });
    window.google.accounts.id.renderButton(el, {
      theme: 'outline',
      size: 'large',
      width: Math.min(320, el.parentElement?.clientWidth || 320),
      text: 'continue_with',
    });
    renderedRef.current = true;
    setAvailable(true);
  }, [initError, scriptReady, resolvedClientId, googleLogin, disabled, onError]);

  return (
    <div className={`auth-google-wrap ${busy ? 'auth-google-wrap--busy' : ''}`} aria-busy={busy}>
      {initError ? (
        <p className="auth-google-hint">{initError}</p>
      ) : !available ? (
        <p className="auth-google-hint" aria-live="polite">
          Loading Google sign-in…
        </p>
      ) : null}
      <div
        ref={btnRef}
        className="auth-google-btn-slot"
        style={{ display: initError ? 'none' : 'flex' }}
      />
      {busy && <p className="auth-google-loading">Signing in with Google…</p>}
    </div>
  );
}
